import { Dropdown, DropdownButton, DropdownHeading, DropdownItemCheckbox, DropdownLabel, DropdownMenu } from '@clay-run/glaze/dropdown/index';
import { Tooltip, TooltipPanel, TooltipTrigger } from '@clay-run/glaze/tooltip';
import type { FlowRole } from '@shared/flow/role';
import { DEFAULT_UI_VIEW, TEAM_DISPLAY_ORDER, teamLabel } from '@shared/flow/role-view';
import { useRoleView, useSetViewAsRole } from '../../../hooks/useRoleView';

const SWATCH_CLASS: Record<FlowRole, string> = {
  claydr: 'bg-bg-red-active',
  gtme: 'bg-bg-blue-active',
  gs: 'bg-bg-green-active',
};

function Swatch({ team }: { team: FlowRole }) {
  return <span aria-hidden className={`mr-2 size-3 shrink-0 rounded-full group-data-[collapsible=icon]:mr-0 ${SWATCH_CLASS[team]}`} />;
}

/**
 * The rail footer's User Role chip: names the role whose defaults the rep is
 * seeing (ClayDR when they have none) and opens the same team picker as the avatar menu. Same pick rule as
 * RailAccountMenu: picking the real team clears the lens, re-picking the
 * current lens writes nothing.
 */
export function UiViewChip() {
  const { team, realTeam, viewingAs, isLoaded } = useRoleView();
  const { setTeam } = useSetViewAsRole();
  if (!isLoaded) return null;

  const lensTeam = viewingAs ? team : null;
  const pick = (option: FlowRole) => {
    // Picking the real team, or ClayDR for a rep with none, is the default: clear the lens.
    const next = option === realTeam || (realTeam === null && option === DEFAULT_UI_VIEW) ? null : option;
    if (next !== lensTeam) setTeam(next);
  };
  // With no team the layouts already resolve to the ClayDR (Prospecting) default,
  // so the chip names the default role as the effective role. `team` itself stays null.
  const effective: FlowRole = team ?? DEFAULT_UI_VIEW;
  const text = `Role: ${teamLabel(effective)}`;
  const label = `User role: ${teamLabel(effective)}`;

  return (
    <Dropdown placement="top-start">
      <Tooltip>
        <TooltipTrigger>
          <DropdownButton
            variant="ghost"
            size="sm"
            aria-label={label}
            data-ui-view-chip
          >
            <Swatch team={effective} />
            <span className="truncate group-data-[collapsible=icon]:hidden">{text}</span>
          </DropdownButton>
        </TooltipTrigger>
        <TooltipPanel>{label}</TooltipPanel>
      </Tooltip>
      <DropdownMenu>
        <DropdownHeading>User Role</DropdownHeading>
        {TEAM_DISPLAY_ORDER.map((option) => (
          <DropdownItemCheckbox key={option} checked={option === effective} onChange={() => pick(option)}>
            <Swatch team={option} />
            <DropdownLabel>{teamLabel(option)}</DropdownLabel>
          </DropdownItemCheckbox>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}
